import { useRef } from "react";

/**
 * Reusable OTP input — built for Android WebView and tested down to 320 px wide.
 *
 * Sizing model: each box is `flex: 1 1 0` with `min-width: 0` and `max-width: 50px`.
 * That means the boxes share whatever horizontal space the parent gives them,
 * shrinking proportionally on tiny screens and capping at 50 px on bigger ones —
 * so 6 boxes ALWAYS fit on a single row, no matter what nested padding the parent has.
 *
 * Styling lives in /app/frontend/src/index.css (`.otp-container`, `.otp-box`)
 * so it can be tweaked in one place and is immune to inline-style overrides.
 */
export default function OTPInput({
  length = 6,
  value = "",
  onChange,
  onComplete,
  autoFocus = true,
  testIdPrefix = "otp-input",
}) {
  const inputs = useRef([]);

  const safeValue = String(value || "").slice(0, length);

  const updateValue = (chars) => {
    const next = chars.slice(0, length).join("");
    onChange?.(next);
    if (next.length === length && next.replace(/\D/g, "").length === length) {
      onComplete?.(next);
    }
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      const arr = safeValue.split("");
      arr[index] = "";
      updateValue(arr);
      return;
    }
    const arr = safeValue.padEnd(length, " ").split("");
    arr[index] = raw[raw.length - 1];
    updateValue(arr.map((c) => (c === " " ? "" : c)));
    if (index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const arr = safeValue.padEnd(length, " ").split("");
      if (!arr[index] || arr[index] === " ") {
        if (index > 0) {
          arr[index - 1] = "";
          updateValue(arr.map((c) => (c === " " ? "" : c)));
          inputs.current[index - 1]?.focus();
        }
      } else {
        arr[index] = "";
        updateValue(arr.map((c) => (c === " " ? "" : c)));
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasted) return;
    const arr = pasted.split("");
    while (arr.length < length) arr.push("");
    updateValue(arr);
    inputs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="otp-container" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputs.current[index] = el)}
          className="otp-box"
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={safeValue[index] || ""}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          autoFocus={autoFocus && index === 0}
          data-testid={`${testIdPrefix}-${index}`}
        />
      ))}
    </div>
  );
}
